/**
 * Zustand store systémového monitoru.
 *
 * Název `system` je obsazený (`store/system.ts` = wsStatus), proto `sysmon`.
 *
 * HISTORIE GRAFŮ TU NENÍ — žije v `lib/sysmon/history.ts` mimo Zustand, protože ring
 * buffer je mutovatelný `Float32Array` a jeho mutace by nikdy nefire-ovala selektor.
 *
 * Diff se aplikuje po TOP-LEVEL SKUPINÁCH (`cpu`, `mem`, `disks`, …), přesně tak, jak ho
 * počítá serverový `diffTelemetry`. Nezměněné skupiny si tím drží referenční identitu,
 * takže selektor na `disks` nefire-uje, když se změnilo jen CPU.
 */

import { create } from "zustand";
import { unpackProcesses } from "@globals/sysmon/procWire";
import { SYSMON_LIMITS, type SysmonProcSortKey } from "@globals/sysmon/types";
import type {
  SysmonHostStateWsEvent,
  SysmonHostSummary,
  SysmonProcess,
  SysmonProcWsEvent,
  SysmonSnapshot,
} from "@client/types/sysmon";

export interface SysmonHostEntry {
  snapshot: SysmonSnapshot | null;
  /** Kdy snapshot DORAZIL (lokální čas) — stáří se počítá z něj, ne z `ts` protějšku. */
  receivedAt: number;
  reachable: boolean;
  error: string | null;
  lastOkTs: number | null;
}

export interface SysmonProcTable {
  hostId: string;
  ts: number;
  rows: SysmonProcess[];
  total: number;
  totalUnfiltered: number;
  truncated: boolean;
  skipped: number;
}

/** Uživatelské předvolby stránky — přežívají přepnutí hostitele. */
export interface SysmonUi {
  sort: SysmonProcSortKey;
  reversed: boolean;
  tree: boolean;
  filter: string;
  includeKernel: boolean;
  limit: number;
  /** Zobrazovat CPU procesů vůči jednomu jádru místo celého stroje (btop `proc_per_core`). */
  perCore: boolean;
  /** Vybrané síťové rozhraní; null = automaticky nejaktivnější. */
  netIface: string | null;
  selectedPid: number | null;
}

interface SysmonStore {
  hosts: SysmonHostSummary[];
  activeHostId: string | null;
  byHost: Map<string, SysmonHostEntry>;
  processes: SysmonProcTable | null;
  ui: SysmonUi;
  loaded: boolean;

  setHosts: (hosts: SysmonHostSummary[]) => void;
  setActiveHost: (hostId: string) => void;
  applySnapshotFull: (hostId: string, snapshot: SysmonSnapshot) => void;
  applySnapshotDiff: (hostId: string, patch: Partial<SysmonSnapshot>) => void;
  applyHostState: (ev: SysmonHostStateWsEvent) => void;
  applyProcesses: (ev: SysmonProcWsEvent) => void;
  patchUi: (patch: Partial<SysmonUi>) => void;
  clear: () => void;
}

const DEFAULT_UI: SysmonUi = {
  sort: "cpu",
  reversed: false,
  tree: false,
  filter: "",
  includeKernel: false,
  limit: SYSMON_LIMITS.procLimitDefault,
  perCore: false,
  netIface: null,
  selectedPid: null,
};

const emptyEntry = (): SysmonHostEntry => ({
  snapshot: null,
  receivedAt: 0,
  reachable: true,
  error: null,
  lastOkTs: null,
});

export const useSysmonStore = create<SysmonStore>((set) => ({
  hosts: [],
  activeHostId: null,
  byHost: new Map(),
  processes: null,
  ui: DEFAULT_UI,
  loaded: false,

  setHosts: (hosts) =>
    set((state) => ({
      hosts,
      loaded: true,
      // Aktivní hostitel se drží; při prvním načtení se vezme lokální.
      activeHostId:
        state.activeHostId && hosts.some((h) => h.id === state.activeHostId)
          ? state.activeHostId
          : (hosts.find((h) => h.local)?.id ?? hosts[0]?.id ?? null),
    })),

  setActiveHost: (hostId) => set({ activeHostId: hostId }),

  applySnapshotFull: (hostId, snapshot) =>
    set((state) => {
      const byHost = new Map(state.byHost);
      const prev = byHost.get(hostId) ?? emptyEntry();
      byHost.set(hostId, { ...prev, snapshot, receivedAt: Date.now(), reachable: true, error: null });
      return { byHost };
    }),

  applySnapshotDiff: (hostId, patch) =>
    set((state) => {
      const prev = state.byHost.get(hostId);
      // Diff bez předchozího plného snapshotu se ignoruje — server pošle full sám.
      if (!prev?.snapshot) return state;
      const byHost = new Map(state.byHost);
      byHost.set(hostId, {
        ...prev,
        snapshot: { ...prev.snapshot, ...patch },
        receivedAt: Date.now(),
        reachable: true,
        error: null,
      });
      return { byHost };
    }),

  applyHostState: (ev) =>
    set((state) => {
      const byHost = new Map(state.byHost);
      const prev = byHost.get(ev.hostId) ?? emptyEntry();
      byHost.set(ev.hostId, {
        ...prev,
        reachable: ev.reachable,
        error: ev.error,
        lastOkTs: ev.lastOkTs,
      });
      const hosts = state.hosts.map((h) =>
        h.id === ev.hostId ? { ...h, reachable: ev.reachable, error: ev.error, lastOkTs: ev.lastOkTs } : h
      );
      return { byHost, hosts };
    }),

  applyProcesses: (ev) =>
    set({
      processes: {
        hostId: ev.hostId,
        ts: ev.ts,
        rows: unpackProcesses(ev.wire),
        total: ev.total,
        totalUnfiltered: ev.totalUnfiltered,
        truncated: ev.truncated,
        skipped: ev.skipped,
      },
    }),

  patchUi: (patch) => set((state) => ({ ui: { ...state.ui, ...patch } })),

  // `ui` se ZÁMĚRNĚ nemaže — je to uživatelská předvolba, ne data.
  clear: () => set({ hosts: [], activeHostId: null, byHost: new Map(), processes: null, loaded: false }),
}));
